import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { safeFetch } from '../utils/requestUtils';
import { getSession } from '../api/supabase';

/**
 * React Query hooks for Stakeholder Groups management
 */

// Helper to get auth headers - async to fetch session
const getAuthHeaders = async () => {
  const session = await getSession();
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Fetch all stakeholder groups
export function useStakeholderGroups() {
  return useQuery({
    queryKey: ['stakeholder-groups'],
    queryFn: async () => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch('/lineage/stakeholders', {
        method: 'GET',
        headers: authHeaders
      });
      return response.data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
    refetchOnMount: 'always' // Always fetch fresh data on component mount
  });
}

// Fetch single stakeholder group
export function useStakeholderGroup(groupId) {
  return useQuery({
    queryKey: ['stakeholder-group', groupId],
    queryFn: async () => {
      if (!groupId) return null;
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}`, {
        method: 'GET',
        headers: authHeaders
      });
      return response.data || null;
    },
    enabled: !!groupId,
    staleTime: 2 * 60 * 1000,
    retry: 2
  });
}

// Create a new stakeholder group
export function useCreateStakeholderGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupData) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch('/lineage/stakeholders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(groupData)
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch stakeholder groups list
      queryClient.invalidateQueries({ queryKey: ['stakeholder-groups'] });
    }
  });
}

// Update stakeholder group
export function useUpdateStakeholderGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, updates }) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(updates)
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-groups'] });
      queryClient.invalidateQueries({ queryKey: ['stakeholder-group', variables.groupId] });
    }
  });
}

// Update notification preferences for a stakeholder group
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, preferences }) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ notification_preferences: preferences })
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-group', variables.groupId] });
    }
  });
}

// Delete stakeholder group
export function useDeleteStakeholderGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch stakeholder groups list
      queryClient.invalidateQueries({ queryKey: ['stakeholder-groups'] });
    }
  });
}

// Fetch group members
export function useGroupMembers(groupId) {
  return useQuery({
    queryKey: ['stakeholder-group-members', groupId],
    queryFn: async () => {
      if (!groupId) return [];
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}/members`, {
        method: 'GET',
        headers: authHeaders
      });
      console.log('useGroupMembers response:', response);
      return response.data || [];
    },
    enabled: !!groupId,
    staleTime: 2 * 60 * 1000,
    retry: 2
  });
}

// Add member to group
export function useAddGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, userId, role }) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({ user_id: userId, role: role || 'member' })
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-group-members', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['stakeholder-group', variables.groupId] });
    }
  });
}

// Remove member from group
export function useRemoveGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, userId }) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}/members/${userId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-group-members', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['stakeholder-group', variables.groupId] });
    }
  });
}

// Fetch assigned objects for group
export function useGroupObjects(groupId) {
  return useQuery({
    queryKey: ['stakeholder-group-objects', groupId],
    queryFn: async () => {
      if (!groupId) return [];
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}/objects`, {
        method: 'GET',
        headers: authHeaders
      });
      console.log('useGroupObjects response:', response);
      return response.data || [];
    },
    enabled: !!groupId,
    staleTime: 2 * 60 * 1000,
    retry: 2
  });
}

// Assign stakeholder group to data object
export function useAssignStakeholderToObject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, dataObjectId, relationshipType, businessImpactLevel, impactDescription }) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          data_object_id: dataObjectId,
          relationship_type: relationshipType || 'consumer',
          business_impact_level: businessImpactLevel || 'medium',
          impact_description: impactDescription
        })
      });
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['stakeholder-group-objects', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['object-lineage', variables.dataObjectId] });
    }
  });
}

// Remove stakeholder group from data object
export function useUnassignStakeholderFromObject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, dataObjectId }) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/lineage/stakeholders/${groupId}/assign`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          data_object_id: dataObjectId
        })
      });
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['stakeholder-group-objects', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['object-lineage', variables.dataObjectId] });
    }
  });
}
